import React, { createContext, useContext, useState } from 'react'
import ConfirmationPopup from 'components/CommonComponents/ConfirmationPopup'
import useLocaleText from 'components/useLocaleText'
import CampaignModal from './CoompetitionModal'
import { CampaignData, CampaignFormValues } from 'redux/actions/campaigns'
import { Campaign } from 'redux/reducers/campaigns'
import OverviewCampaignModal from './OverviewCampaignModal'

interface CampaignModalsContextType {
  openCampaignModal: (opts?: OpenCampaignModalOpts) => void
  requestCloseCampaignModal: () => void
  requestConfirmAction: (callback: () => void) => void
  openOverviewCampaignModal: (campaign: Campaign, campaignData: CampaignData) => void
}

interface OpenCampaignModalOpts {
  onCreated?: (data: CampaignFormValues) => void
  initialData?: CampaignFormValues | null
}

const CampaignModalsContext = createContext<CampaignModalsContextType | null>(null)

export const useCampaignModals = () => {
  const ctx = useContext(CampaignModalsContext)
  if (!ctx) throw new Error('useCampaignModals must be used within CampaignModalsProvider')
  return ctx
}

export const CampaignModalsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const admin_cancel_confirm = useLocaleText('admin_cancel_confirm')
  const admin_cancel_desc = useLocaleText('card_confirm_desc')
  const admin_no_cancel = useLocaleText('admin_no_cancel')
  const admin_yes_cancel = useLocaleText('admin_yes_cancel_button')

  const change_competition_status_confirm = useLocaleText('are_you_sure')
  const t_yes = useLocaleText('yes')
  const t_no = useLocaleText('no')

  const [onCampaignCreated, setOnCampaignCreated] = useState<((data: CampaignFormValues) => void) | null>(null)
  const [campaignModalOpen, setCampaignModalOpen] = useState(false)
  const [overviewCampaignModalOpen, setOverviewCampaignModalOpen] = useState(false)
  const [confirmationOpen, setConfirmationOpen] = useState(false)
  const [initialData, setInitialData] = useState<CampaignFormValues | null>(null)
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null)
  const [actionConfirmationOpen, setActionConfirmationOpen] = useState(false)
  const [campaignOverview, setCampaignOverview] = useState<{
    campaign: Campaign
    campaignData: CampaignData
  } | null>(null)

  const openCampaignModal = (opts?: OpenCampaignModalOpts) => {
    setOnCampaignCreated(() => opts?.onCreated || null)
    setInitialData(opts?.initialData || null)
    setCampaignModalOpen(true)
  }

  const requestCloseCampaignModal = () => {
    setConfirmationOpen(true)
  }

  const closeCampaignModal = () => {
    setCampaignModalOpen(false)
    setOnCampaignCreated(null)
    setInitialData(null)
  }

  const confirmClose = () => {
    closeCampaignModal()
    setConfirmationOpen(false)
  }

  const requestConfirmAction = (callback: () => void) => {
    setPendingAction(() => callback)
    setActionConfirmationOpen(true)
  }

  const confirmAction = () => {
    if (pendingAction) {
      pendingAction()
    }
    setActionConfirmationOpen(false)
    setPendingAction(null)
    confirmClose()
  }

  const cancelAction = () => {
    setActionConfirmationOpen(false)
    setPendingAction(null)
  }

  const cancelClose = () => setConfirmationOpen(false)

  const openOverviewCampaignModal = (campaign: Campaign, campaignData: CampaignData) => {
    setCampaignOverview({ campaign, campaignData })
    setOverviewCampaignModalOpen(true)
  }

  return (
    <CampaignModalsContext.Provider
      value={{ openCampaignModal, requestCloseCampaignModal, requestConfirmAction, openOverviewCampaignModal }}
    >
      {children}

      {campaignModalOpen && (
        <CampaignModal
          isOpen={campaignModalOpen}
          onClose={closeCampaignModal}
          onRequestClose={requestCloseCampaignModal}
          onCreated={onCampaignCreated || (() => {})}
          campaign={initialData}
        />
      )}

      {confirmationOpen && (
        <ConfirmationPopup
          isOpen
          onCancel={cancelClose}
          onConfirm={confirmClose}
          isLoading={false}
          title={admin_cancel_confirm}
          description={admin_cancel_desc}
          cancelButtonText={admin_no_cancel}
          confirmButtonText={admin_yes_cancel}
        />
      )}

      {actionConfirmationOpen && (
        <ConfirmationPopup
          isOpen
          onCancel={cancelAction}
          onConfirm={confirmAction}
          isLoading={false}
          title={change_competition_status_confirm}
          cancelButtonText={t_no}
          confirmButtonText={t_yes}
        />
      )}

      {overviewCampaignModalOpen && campaignOverview && (
        <OverviewCampaignModal
          isOpen={overviewCampaignModalOpen}
          onClose={() => setOverviewCampaignModalOpen(false)}
          campaign={campaignOverview?.campaign}
          campaignData={campaignOverview?.campaignData}
        />
      )}
    </CampaignModalsContext.Provider>
  )
}
